/**
 * Lab-admin formatting helpers. Money is integer CENTIMES on the wire (rounding-safe, fiscal) — the
 * cockpit reuses the finance money helpers verbatim (reuse over reinvent, L-5) rather than re-deriving
 * the centimes↔display conversion. Dates render locale-aware (FR/AR) with the numeric face kept LTR.
 */
export {
  CURRENCY,
  formatCentimes,
  parseToCentimes,
  centimesToEditable,
} from "@/features/finance/money";

import type { Locale } from "@/app";
import type { BadgeTone } from "@/ui";

/**
 * Map a central subscription statut to a Badge tone. Lives here (not in a screen) so both the overview
 * KPI tile and the subscription page consume it without tripping the react-refresh single-export rule.
 *   ACTIVE → success · ESSAI → info · PAST_DUE → warn · SUSPENDUE/EXPIREE/ANNULEE → danger · else neutral
 */
export function statutTone(statut: string | null | undefined): BadgeTone {
  switch (statut) {
    case "ACTIVE":
      return "success";
    case "ESSAI":
      return "info";
    case "PAST_DUE":
      return "warn";
    case "SUSPENDUE":
    case "EXPIREE":
    case "ANNULEE":
      return "danger";
    default:
      return "neutral";
  }
}

/** Map our app Locale to a BCP-47 tag for Intl. AR uses the Algerian Arabic locale. */
function bcp47(locale: Locale): string {
  return locale === "ar" ? "ar-DZ" : "fr-DZ";
}

/** ISO timestamp → localized date+time, e.g. "27/06/2026 14:32". Returns "—" on a missing value. */
export function formatDateTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp47(locale), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

/** ISO timestamp → localized date only, e.g. "27/06/2026". */
export function formatDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp47(locale), { dateStyle: "medium" }).format(d);
}

/** Truncate a long JSON diff to a compact one-line preview for a table cell. */
export function previewJson(blob: unknown, max = 80): string {
  if (blob == null) return "—";
  let s: string;
  try {
    s = typeof blob === "string" ? blob : JSON.stringify(blob);
  } catch {
    return "—";
  }
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** A short, stable, human label for an actor id when the name is not joined (the journal carries ids). */
export function actorLabel(utilisateurId: string | null | undefined): string {
  return utilisateurId ? `#${utilisateurId}` : "—";
}
